import { useState } from "react";
import { CalendarEvent, CalenderSource } from "../Types/CalendarEvent";
import { createEventRequest, time } from '../Types/RequestResponse/createEventResponse';
import { EventService } from '../Services/EventService';
import addMinutes from "date-fns/addMinutes";
import { getTimeZones } from "@vvo/tzdb";
import { getUtcDate, toClockTimeString } from "../Helpers/DateHelpers";
import { TextService } from "../Services/TextService";
import { EventType } from "../Types/EventType";
import { TimeChoice } from "../Types/TimeChoice";

export const useCreateEvent = () => {
  const [events, setEvents] = useState([] as CalendarEvent[]);
  const [step, setStep] = useState(60 as number);
  const [customStep, setCustomStep] = useState(60 as number);
  const [maxParticipants, setMaxParticipants] = useState(25 as number);
  const [allowWaitlist, setAllowWaitlist] = useState(false as boolean);
  const [maxWaitlist, setMaxWaitlist] = useState(null as number | null);
  const [membersOnly, setMembersOnly] = useState(false as boolean);
  const [showOnMemberSite, setShowOnMemberSite] = useState(false as boolean);
  const [eventType, setEventType] = useState(EventType.SignUp as EventType);
  const [timeChoice, setTimeChoice] = useState(TimeChoice.Several as TimeChoice);
  const [timeZone, setTimeZone] = useState(String);
  const [timeZones, setTimeZones] = useState([] as any[]);
  const [deadline, setDeadline] = useState("" as string);
  // The participant list is hidden by default, so names and answers are only shown when the organizer chooses to.
  const [displayType, setDisplayType] = useState(1 as number);
  const [automaticReminder, setAutomaticReminder] = useState(false as boolean);
  // Days before the deadline (or the first time) the automatic reminder is sent.
  const [automaticReminderDays, setAutomaticReminderDays] = useState(2);
  const GetText = TextService.GetText;

  const CreateEvent = async (formValues: any) => {
    let times: time[] = [];
    // Only the event's own times, not the admin's Google events shown in the calendar.
    events.filter(p => p.source === CalenderSource.EventBooky).forEach(function (event) {
      times.push({ start: event.start, end: event.end, startUtc: getUtcDate(event.start, timeZone), endUtc: getUtcDate(event.end, timeZone), isFullDay: event.allDay })
    });

    const createEventRequest: createEventRequest = {
      name: formValues.name,
      timezoneid: timeZone,
      displayType: displayType,
      answerOptions: formValues.answerOptions,
      deadline: deadline !== "" ? toClockTimeString(deadline) : undefined,
      maxParticipants: maxParticipants,
      allowWaitlist: allowWaitlist,
      maxWaitlist: maxWaitlist ?? undefined,
      membersOnly: membersOnly,
      showOnMemberSite: showOnMemberSite,
      eventType: eventType,
      timeChoice: events.filter(p => p.source === CalenderSource.EventBooky).length > 1 ? timeChoice : TimeChoice.Several,
      description: formValues.eventDescription,
      location: formValues.eventLocation,
      sendAutomaticReminder: automaticReminder,
      automaticReminderDays: automaticReminderDays,
      times: times
    }

    return await EventService.CreateEvent(createEventRequest);
  };

  const CreateEventItem = async (slot: any) => {
    if (slot.action === "click") {
      let event: CalendarEvent = {
        title: "",
        id: (new Date()).getTime().toString(),
        allDay: step == -2 ? true : false,
        desc: '',
        start: slot.start,
        end: addMinutes(new Date(slot.start), step == -1 ? customStep : step),
        source: CalenderSource.EventBooky
      }
      events.push(event);

      setEvents(events => ([
        ...events,

      ]));
    }
  }

  // Adds a time that is known beforehand, for an event that starts from a chosen time.
  const AddTime = (start: Date, end: Date, allDay: boolean) => {
    setEvents(events => ([
      ...events,
      {
        title: allDay ? GetText("Calender_AllDay") : "",
        id: (new Date()).getTime().toString(),
        allDay: allDay,
        desc: '',
        start: start,
        end: end,
        source: CalenderSource.EventBooky
      }
    ]));
  }

  const SetGoogleEvents = async (googleEvents: any[]) => {

    if (!googleEvents) {
      return;
    }

    var newEvents = [...events]; // make a separate copy of the array
    newEvents = events.filter(p => p.source == CalenderSource.EventBooky);

    googleEvents.forEach(googleEvent => {
      if (googleEvent.start.dateTime != undefined) {

        let event: CalendarEvent = {
          title: googleEvent.summary,
          id: googleEvent.id,
          allDay: false,
          desc: googleEvent.summary,
          start: new Date(googleEvent.start.dateTime),
          end: new Date(googleEvent.end.dateTime),
          source: CalenderSource.Google
        }
        newEvents.push(event);
      }
    });

    setEvents(newEvents);
  }

  const UpdateEvent = (slot: any) => {
    events.forEach(function (event, index) {
      if (event.id === slot.event.id) {
        events[index].start = slot.start;
        events[index].end = slot.end;
      }
    });

    setEvents(events => ([
      ...events,

    ]));
  }

  const SetFullDayOnEvent = (id: string, isFullDay: boolean) => {
    events.forEach(function (event, index) {
      if (event.id === id) {
        events[index].allDay = isFullDay;
        events[index].title = isFullDay ? GetText("Calender_AllDay") : ""
      }
    });

    setEvents(events => ([
      ...events,

    ]));
  }

  const DeleteEvent = (id: string) => {
    setEvents(events => ([
      ...events.filter(item => item.id !== id),

    ]));
  }
  const changeStep = (step: number) => {
    setStep(step);
  }

  const LoadTimeZones = () => {
    const timeZonesWithUtc = getTimeZones({ includeUtc: false });
    let timeZones: any = [];
    timeZonesWithUtc.forEach(zone => {
      let selectLabel = zone.countryName + " (" + zone.mainCities.toString() + ")";
      timeZones.push({ "label": selectLabel, "value": zone.name })

      if (zone.name.toLocaleLowerCase() === Intl.DateTimeFormat().resolvedOptions().timeZone.toLocaleLowerCase()) {
        setTimeZone(zone.name);
      }
    });

    timeZones = timeZones.sort((a: any, b: any) => (a.label < b.label) ? -1 : 1);

    setTimeZones(timeZones);
  }

  return {
    CreateEvent,
    CreateEventItem,
    UpdateEvent,
    DeleteEvent,
    events,
    step,
    timeZone,
    timeZones,
    setTimeZone,
    changeStep,
    LoadTimeZones,
    SetFullDayOnEvent,
    maxParticipants,
    setMaxParticipants,
    allowWaitlist,
    setAllowWaitlist,
    maxWaitlist,
    setMaxWaitlist,
    membersOnly,
    setMembersOnly,
    showOnMemberSite,
    setShowOnMemberSite,
    eventType,
    setEventType,
    timeChoice,
    setTimeChoice,
    AddTime,
    deadline,
    setDeadline,
    displayType,
    setDisplayType,
    automaticReminder,
    setAutomaticReminder,
    automaticReminderDays,
    setAutomaticReminderDays,
    SetGoogleEvents,
    customStep,
    setCustomStep
  };
};